import React, { useMemo } from 'react';
import _isEmpty from 'lodash-es/isEmpty';
import {
  Column,
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  Row,
  RowData,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import { NewTable } from './NewTable';
import { LSUNote, Position } from './Position';
import {
  LivretEleve,
  LivretEleveDomaine,
  LivretEleveMatiere,
  TypePositionnement,
} from '@teetsh/app/src/containers/Livrets/types/livret';
import { UpdateLivretEleveMatiereDTO } from '@teetsh/app/src/containers/Livrets/queries/useUpdateLivretEleveMatiere';
import { matieresToTableRows } from './matieresToTableRows';
import { NotationSystem } from '@teetsh/app/src/types/notationSystem';
import { Competence } from './Competence';
import { getCompetencesOfAMatiere } from './getCompetencesOfAMatiere';
import { findByNom } from './findByNom';
import { Matiere } from '@teetsh/app/src/types/bo';
import { AddCompetenceButton } from './AddCompetenceButton';
import { Remark } from './Remark';
import { CompetenceItem } from './types';
import { UpdateLivretEleveCompetenceDTO } from '@teetsh/app/src/containers/Livrets/queries/competences/useUpdateLivretCompetence';
import { useTranslation } from 'react-i18next';

interface Props {
  typePositionnement: TypePositionnement;
  livretEleve: LivretEleve;
  livretEvalId: string;

  boMatieres: Matiere[];
  notationSystem: NotationSystem;
  onUpdateMatiere?: (matiere: UpdateLivretEleveMatiereDTO) => void;
  onUpdateCompetence?: (competence: UpdateLivretEleveCompetenceDTO) => void;
  withIA?: boolean;
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    onUpdateMatiere?: (matiere: UpdateLivretEleveMatiereDTO) => void;
    onUpdateCompetence?: (competence: UpdateLivretEleveCompetenceDTO) => void;
    matieres: LivretEleveMatiere[];
    typePositionnement: TypePositionnement;
    withIA?: boolean;

    livretEleveId: string;
    livretEvalId: string;
    boMatieres: Matiere[];
    notationSystem: NotationSystem;
    readonly: boolean;
  }
}

function findMatiereById(
  id: string,
  matieres: LivretEleveMatiere[],
): LivretEleveMatiere | undefined {
  return (matieres || []).find(m => m.id === id);
}

function findDomaineById(
  id: string,
  matiereId: string,
  matieres: LivretEleveMatiere[],
): LivretEleveDomaine | undefined {
  const matiere = findMatiereById(matiereId, matieres);
  return (matiere?.domaines || []).find(d => d.id === id);
}

interface WrapperProps {
  table: Table<any>;
  row: Row<any>;
  getValue: () => any;
  renderValue: () => any;
  column: Column<any, any>;
}

function RemarkCell(props: WrapperProps) {
  if (!props.table.options.meta) {
    return null;
  }
  const { onUpdateMatiere, matieres, readonly, livretEleveId, withIA } =
    props.table.options.meta;
  const matiereId = props.row.original?.matiereId;
  const matchingMatiere = findMatiereById(matiereId, matieres);
  if (!matchingMatiere) {
    return null;
  }
  return (
    <Remark
      livretEleveId={livretEleveId}
      withIA={withIA}
      readonly={readonly}
      matiereId={matiereId}
      onChange={r =>
        onUpdateMatiere &&
        onUpdateMatiere({
          ...matchingMatiere,
          remark: r,
        })
      }
      value={props.getValue() || ''}
    />
  );
}

function MatiereCell(props: WrapperProps) {
  if (!props.table.options.meta) {
    return null;
  }
  const { matieres } = props.table.options.meta;
  const matiereId = props.getValue();
  if (!matiereId) {
    return null;
  }
  const matchingMatiere = findMatiereById(matiereId, matieres);
  if (!matchingMatiere) {
    return null;
  }
  return matchingMatiere?.name;
}

function DomaineCell(props: WrapperProps) {
  if (!props.table.options.meta) {
    return null;
  }
  const { matieres } = props.table.options.meta;
  const domaineId = props.getValue();
  const matiereId = props.row.original?.matiereId;
  if (!domaineId || !matiereId) {
    return null;
  }
  const matchingDomaine = findDomaineById(domaineId, matiereId, matieres);
  if (!matchingDomaine) {
    return null;
  }
  return matchingDomaine?.name;
}

function CompetenceCell(props: WrapperProps) {
  if (!props.table.options.meta) {
    return null;
  }
  const { matieres, livretEvalId, livretEleveId, boMatieres, readonly } =
    props.table.options.meta;

  const matiereId = props.row.original?.matiereId;
  const domaineId = props.row.original?.domaineId;
  const typeCompetenceItem = props?.row?.original?.type;

  const matchingMatiere = findMatiereById(matiereId, matieres);
  const matchingDomaine = findDomaineById(domaineId, matiereId, matieres);
  if (!matchingMatiere) {
    return null;
  }
  const selectedCompetences =
    matchingDomaine?.competences || matchingMatiere?.competences || [];

  const matiereFixed = (matchingMatiere?.name || '')
    .toLowerCase()
    .startsWith('langue vivante')
    ? 'Langue vivantes'
    : matchingMatiere?.name;

  const matchingBoMatiere = findByNom(matiereFixed, boMatieres);

  // Pour les matières artistiques dans le BO teetsh les matières sont des domaines ... (pour le cycle 3)
  const isSearchByDomaine =
    matchingMatiere?.name === 'Enseignements artistiques' &&
    !matchingBoMatiere &&
    !!matchingDomaine?.name;

  const matiereCompetences = isSearchByDomaine
    ? getCompetencesOfAMatiere(
        findByNom(matchingDomaine.name, boMatieres),
        undefined,
      )
    : getCompetencesOfAMatiere(matchingBoMatiere, matchingDomaine?.name);

  const options = matiereCompetences
    .map(com => ({
      label: com.nom,
      value: com.nom,
    }))
    .filter(opt => !selectedCompetences.map(c => c.name).includes(opt.value));

  if (readonly) {
    return <div>{props.getValue()?.name}</div>;
  }
  if (typeCompetenceItem === 'add-competence') {
    return (
      <AddCompetenceButton
        livretEvalId={livretEvalId}
        livretEleveId={livretEleveId}
        options={options}
        matiereId={matiereId}
        domaineId={domaineId}
      />
    );
  }

  return (
    <Competence
      competence={props.getValue()}
      options={options}
      livretEvalId={livretEvalId}
      livretEleveId={livretEleveId}
      matiereId={matiereId}
      domaineId={domaineId}
    />
  );
}

function PositionCell(props: WrapperProps) {
  if (!props.table.options.meta) {
    return null;
  }
  const {
    onUpdateMatiere,
    matieres,
    notationSystem,
    onUpdateCompetence,
    typePositionnement,
  } = props.table.options.meta;

  const matiereId = props.row.original?.matiereId;
  const domaineId = props.row.original?.domaineId;
  const competenceId = props.row.original?.competence?.id;
  const competenceName = props.row.original?.competence?.name;
  const value = (props.column.columnDef.meta as any)?.value;
  if (
    props.row.original?.type === 'add-competence' &&
    typePositionnement === 'competence'
  ) {
    return null;
  }

  const onChange = v => {
    if (!onUpdateMatiere) {
      return null;
    }
    const matchingMatiere = findMatiereById(matiereId, matieres);
    const matchingDomaine = findDomaineById(domaineId, matiereId, matieres);
    if (!matchingMatiere || !matiereId) {
      return null;
    }
    if (matchingDomaine && typePositionnement === 'domaine') {
      const newDomaines = matchingMatiere.domaines.map(d => {
        if (d.id === domaineId) {
          return {
            ...d,
            note: v,
          };
        }
        return d;
      });
      return onUpdateMatiere({
        ...matchingMatiere,
        domaines: newDomaines,
      });
    }
    if (typePositionnement === 'competence') {
      if (!onUpdateCompetence || _isEmpty(competenceName)) {
        return null;
      }
      return onUpdateCompetence({
        id: competenceId,
        note: v,
        matiereId,
        domaineId,
        name: competenceName,
      });
    }
    return onUpdateMatiere({
      ...matchingMatiere,
      note: v,
    });
  };
  return (
    <Position
      onChange={!onUpdateMatiere && !onUpdateCompetence ? undefined : onChange}
      notationSystem={notationSystem}
      note={props.getValue()}
      lsuNote={value}
    />
  );
}

export function LivretTable(props: Props) {
  const {
    livretEleve,
    onUpdateMatiere,
    onUpdateCompetence,
    typePositionnement,
    notationSystem,
    livretEvalId,
    withIA,
  } = props;
  const { t } = useTranslation(['carnet', 'common']);
  const readonly = !onUpdateMatiere;
  const columns = useMemo<ColumnDef<CompetenceItem>[]>(
    () => [
      {
        id: 'matiereId',
        accessorKey: 'matiereId',
        header: () => null,
        cell: MatiereCell,
        meta: {
          hide: true,
          size: '3%',
        },
      },

      {
        id: 'domaineId',
        accessorKey: 'domaineId',
        header: () => t('carnet:livret.domainesEnseignement'),
        cell: DomaineCell,
        meta: {
          colSpan: 2,
          size: '16%',
        },
      },
      {
        id: 'competence',
        accessorKey: 'competence',
        cell: CompetenceCell,
        header: () => (
          <span>
            {t('carnet:livret.elementsProgrammeTravailles').toUpperCase()}
          </span>
        ),
        meta: {
          size: '32%',
          condensed: true,
        },
      },
      {
        id: 'remark',
        accessorKey: 'remark',
        header: t('carnet:livret.acquisitionsProgress').toUpperCase(),
        footer: info => info.column.id,
        cell: RemarkCell,
        meta: {
          size: '31.5%',
          aggregateBy: 'matiereId',
          verticalAlign: 'top',
        },
      },
      {
        id: 'positions',
        header: () => (
          <span>{t('carnet:livret.objectifApprentissage').toUpperCase()}</span>
        ),
        columns: [
          {
            value: 'NA' as LSUNote,
            label: t('common:notations.naLong'),
          },
          {
            value: 'PA' as LSUNote,
            label: t('common:notations.paLong'),
          },
          {
            value: 'A' as LSUNote,
            label: t('common:notations.aLong'),
          },
          {
            value: 'D' as LSUNote,
            label: t('common:notations.dLong'),
          },
        ].map(({ value, label }) => ({
          id: value,
          accessorKey: 'note',
          cell: PositionCell,
          header: () => label,
          meta: {
            size: '3.5%',
            aggregateBy:
              typePositionnement === 'domaine' ? 'domaineId' : undefined,
            value,
            label,
            readonly: !!onUpdateCompetence,
            rotated: true,
          },
        })),
      },
    ],
    [JSON.stringify(livretEleve.matieres)],
  );

  const grouping = useMemo(() => ['matiereId', 'domaineId'], []);
  const columnOrder = useMemo(
    () =>
      typePositionnement === 'competence'
        ? [
            'matiereId',
            'domaineId',
            'competence',
            'positions',
            'NA',
            'PA',
            'A',
            'D',
            'remark',
          ]
        : [
            'matiereId',
            'domaineId',
            'competence',
            'remark',
            'positions',
            'NA',
            'PA',
            'A',
            'D',
          ],
    [],
  );
  const data = useMemo(
    () =>
      matieresToTableRows(typePositionnement, livretEleve.matieres, readonly),
    [JSON.stringify(livretEleve.matieres)],
  );
  const table = useReactTable({
    data,
    columns,
    state: {
      grouping,
      expanded: true,
      columnOrder,
    },
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    meta: {
      onUpdateMatiere,
      onUpdateCompetence,
      typePositionnement,
      matieres: livretEleve.matieres,
      livretEleveId: livretEleve.id,
      livretEvalId,
      boMatieres: props.boMatieres,
      readonly,
      withIA,
      notationSystem,
    },
  });

  return <NewTable tableInstance={table} />;
}
